'use client'

import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/lib/utils'

/**
 * Message + skeleton graphic used while a long-running report job is in progress.
 */
export const ProcessingAnalysisCardBody = () => (
  <div className="py-8 text-center" role="status" aria-live="polite">
    <Skeleton className="mx-auto mb-4 h-12 w-12 rounded-full" aria-hidden />
    <h3 className="mb-2 text-lg font-semibold text-gray-900">Processing analysis</h3>
    <p className="text-gray-600">
      Results are being generated. This may take up to 20 minutes to complete. You may visit this
      page later to see the results.
    </p>
  </div>
)

export type ReportShellSkeletonProps = {
  className?: string
}

/**
 * Placeholder layout for full-width report / dashboard content while data loads.
 */
export const ReportShellSkeleton = ({ className }: ReportShellSkeletonProps) => (
  <div
    className={cn('flex w-full max-w-5xl flex-col gap-6 px-4 py-6 md:px-8', className)}
    aria-busy="true"
    aria-label="Loading content"
  >
    <div className="flex flex-wrap items-center gap-3">
      <Skeleton className="h-9 w-44 rounded-lg" />
      <Skeleton className="h-8 w-24 rounded-full" />
      <Skeleton className="h-8 w-28 rounded-full" />
      <Skeleton className="h-8 w-32 rounded-full" />
    </div>
    <Skeleton className="h-10 w-full max-w-xl rounded-md" />
    <div className="grid gap-4 md:grid-cols-2">
      <Skeleton className="h-40 rounded-lg" />
      <Skeleton className="h-40 rounded-lg" />
    </div>
    <Skeleton className="h-52 w-full rounded-lg" />
  </div>
)

export type ListingCardsGridSkeletonProps = {
  className?: string
  cardCount?: number
}

export const ListingCardsGridSkeleton = ({
  className,
  cardCount = 6,
}: ListingCardsGridSkeletonProps) => (
  <div
    className={cn('grid grid-cols-1 gap-4 md:grid-cols-2', className)}
    aria-busy="true"
    aria-label="Loading cards"
  >
    {Array.from({ length: cardCount }).map((_, i) => (
      <div key={i} className="space-y-3 rounded-lg border border-border bg-card p-5">
        <Skeleton className="h-5 w-full max-w-sm rounded-md" />
        <Skeleton className="h-4 w-full rounded-md" />
        <Skeleton className="h-4 w-full max-w-md rounded-md" />
        <div className="flex gap-2 pt-2">
          <Skeleton className="h-6 w-20 rounded-full" />
          <Skeleton className="h-6 w-16 rounded-full" />
        </div>
      </div>
    ))}
  </div>
)

export type ListingShellSkeletonProps = {
  className?: string
  /** Number of placeholder cards in the grid */
  cardCount?: number
}

/**
 * Page-level skeleton for list/grid views (reports, audits, citations index).
 */
export const ListingShellSkeleton = ({
  className,
  cardCount = 6,
}: ListingShellSkeletonProps) => (
  <div
    className={cn('mx-auto w-full max-w-6xl px-4 py-6 md:px-8', className)}
    aria-busy="true"
    aria-label="Loading list"
  >
    <div className="mb-6 space-y-3">
      <Skeleton className="h-9 w-56 rounded-md" />
      <Skeleton className="h-4 w-full max-w-xl rounded-md" />
      <Skeleton className="h-4 w-full max-w-md rounded-md" />
    </div>
    <ListingCardsGridSkeleton cardCount={cardCount} />
  </div>
)
